import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Eye, LayoutDashboard, MoreVertical, Pencil, Plus, RefreshCw, Save, Trash2, X } from 'lucide-react'
import { toast } from 'sonner'
import { ApiError } from '@/api/client'
import { useUserRole } from '@/hooks/useUserRole'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Field, FieldLabel } from '@/components/ui/field'
import DateInput from '@/components/inputs/DateInput'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { platformApi } from '../api'
import type { Form } from '../types'
import { dashboardApi } from './api'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
    CUSTOM_PERIOD, EDIT_FLAG, PERIODS, defaultSpan, hasFilter, isPeriod, moveItem, newWidgetId, periodHint, periodOverride, withWidgets,
    type Dashboard, type PeriodChoice, type Widget,
} from './model'
import { useWidgetData, type Overrides } from './useWidgetData'
import WidgetEditor from './WidgetEditor'
import { WidgetTile } from './WidgetTile'

// Literal class names so Tailwind can see them.
const SPAN_CLASS = { 1: '', 2: 'md:col-span-2', 3: 'md:col-span-2 xl:col-span-3 2xl:col-span-4' } as const

const ALL_DEPTS = '__all'

interface Draft { name: string; description: string; shared: 'private' | 'org'; widgets: Widget[] }

export default function DashboardPage({ dashboardId }: { dashboardId: string }) {
    const { isRiskManager } = useUserRole()
    const navigate = useNavigate()
    const [dash, setDash] = useState<Dashboard | null>(null)
    const [canEdit, setCanEdit] = useState(false)
    const [loadError, setLoadError] = useState('')
    const [editing, setEditing] = useState(false)
    const [draft, setDraft] = useState<Draft | null>(null)
    const [forms, setForms] = useState<Form[]>([])
    const [period, setPeriod] = useState<PeriodChoice>('all')
    const [dept, setDept] = useState('')
    const [depts, setDepts] = useState<string[]>([])
    const [from, setFrom] = useState('')
    const [to, setTo] = useState('')
    const [version, setVersion] = useState(0)
    const [saving, setSaving] = useState(false)
    const [problems, setProblems] = useState<string[]>([])
    const [saveError, setSaveError] = useState('')
    const [editor, setEditor] = useState<{ open: boolean; widget: Widget | null }>({ open: false, widget: null })
    const [removeId, setRemoveId] = useState<string | null>(null)
    const [deleteOpen, setDeleteOpen] = useState(false)
    const [deleting, setDeleting] = useState(false)

    const range = useMemo<Overrides>(() => {
        const o: Overrides = {}
        const dr = periodOverride(period, from, to)
        if (dr) o.dateRange = dr
        if (dept) o.filters = { field: '$department', op: 'equals', value: dept }
        return o
    }, [period, from, to, dept])
    const periodProblem = periodHint(period, from, to)
    const widgets = editing && draft ? draft.widgets : dash?.layout?.widgets ?? []
    const { slots, refresh } = useWidgetData(dashboardId, widgets, editing, range, version)

    const startEdit = useCallback((d: Dashboard) => {
        setDraft({ name: d.name, description: d.description, shared: d.shared, widgets: d.layout?.widgets ?? [] })
        setProblems([]); setSaveError(''); setEditing(true)
        platformApi.listForms().then(setForms).catch((e) => toast.error((e as Error).message))
    }, [])

    useEffect(() => {
        let live = true
        dashboardApi.get(dashboardId)
            .then((r) => {
                if (!live) return
                setDash(r.dashboard); setCanEdit(r.can_edit)
                setPeriod(isPeriod(r.dashboard.layout?.period) ? r.dashboard.layout.period : 'all')
                if (hasFilter(r.dashboard.layout, 'department')) {
                    dashboardApi.previewWidget({ id: 'dept-options', title: 'Business units', type: 'bar', groupBy: { kind: 'department' }, limit: 500, sort: 'label' })
                        .then((res) => live && setDepts(res.series.map((p) => p.label).filter(Boolean)))
                        .catch(() => { /* filter simply stays empty */ })
                }
                let flagged = false
                try { flagged = sessionStorage.getItem(EDIT_FLAG + dashboardId) === '1'; sessionStorage.removeItem(EDIT_FLAG + dashboardId) } catch { /* ignore */ }
                if (flagged && r.can_edit) startEdit(r.dashboard)
            })
            .catch((e) => live && setLoadError((e as Error).message))
        return () => { live = false }
    }, [dashboardId, startEdit])

    const setWidgets = (fn: (w: Widget[]) => Widget[]) => setDraft((d) => (d ? { ...d, widgets: fn(d.widgets) } : d))

    const save = async () => {
        if (!draft) return
        setSaving(true); setProblems([]); setSaveError('')
        try {
            const updated = await dashboardApi.update(dashboardId, {
                name: draft.name.trim(), description: draft.description, shared: draft.shared, layout: withWidgets(dash?.layout, draft.widgets),
            })
            setDash(updated); setEditing(false); setVersion((v) => v + 1)
            toast.success('Dashboard saved')
        } catch (e) {
            const a = e as ApiError
            setSaveError(a.message); setProblems(a.problems ?? [])
        } finally { setSaving(false) }
    }

    const exportCsv = async (w: Widget) => {
        try {
            const blob = await dashboardApi.exportCsv(dashboardId, w.id)
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url; a.download = `${w.title.replace(/[^A-Za-z0-9_-]+/g, '_') || w.id}.csv`
            document.body.appendChild(a); a.click(); a.remove()
            URL.revokeObjectURL(url)
        } catch (e) { toast.error((e as Error).message) }
    }

    const remove = async () => {
        setDeleting(true)
        try {
            await dashboardApi.remove(dashboardId)
            navigate({ to: '/dashboards' })
        } catch (e) { toast.error((e as Error).message); setDeleting(false) }
    }

    const openRow = (id: string) => navigate({ to: '/submissions/$id', params: { id } })

    if (loadError) {
        return (
            <div className="mx-auto max-w-xl py-8">
                <Empty>
                    <EmptyHeader><EmptyTitle>Dashboard unavailable</EmptyTitle><EmptyDescription>{loadError}</EmptyDescription></EmptyHeader>
                    <EmptyContent><Button asChild variant="outline"><Link to="/dashboards">Back to dashboards</Link></Button></EmptyContent>
                </Empty>
            </div>
        )
    }
    if (!dash) {
        return (
            <div className="flex flex-col gap-4">
                <Skeleton className="h-8 w-64" />
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-56" />)}</div>
            </div>
        )
    }

    const removeTarget = widgets.find((w) => w.id === removeId)

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3">
                <Link to="/dashboards" className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="size-4" aria-hidden /> Dashboards
                </Link>
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                        {editing && draft ? (
                            <div className="flex max-w-xl flex-col gap-3">
                                <Field>
                                    <FieldLabel htmlFor="dash-name">Name</FieldLabel>
                                    <Input id="dash-name" value={draft.name} maxLength={200} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                                </Field>
                                <Field>
                                    <FieldLabel htmlFor="dash-desc">Description</FieldLabel>
                                    <Textarea id="dash-desc" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
                                </Field>
                                <Field orientation="horizontal">
                                    <Switch id="dash-shared" checked={draft.shared === 'org'} onCheckedChange={(c) => setDraft({ ...draft, shared: c ? 'org' : 'private' })} />
                                    <FieldLabel htmlFor="dash-shared" className="font-normal">Share with the organisation</FieldLabel>
                                </Field>
                            </div>
                        ) : (
                            <>
                                <div className="flex flex-wrap items-center gap-2">
                                    <h1 className="text-2xl font-semibold tracking-tight">{dash.name}</h1>
                                    <Badge variant={dash.shared === 'org' ? 'secondary' : 'outline'}>{dash.shared === 'org' ? 'Shared' : 'Private'}</Badge>
                                </div>
                                {dash.description && <p className="text-sm text-muted-foreground">{dash.description}</p>}
                            </>
                        )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        {editing ? (
                            <>
                                <Button variant="outline" onClick={() => setEditor({ open: true, widget: null })}><Plus data-icon="inline-start" />Add widget</Button>
                                <Button variant="outline" onClick={() => { setEditing(false); setProblems([]); setSaveError('') }} disabled={saving}><X data-icon="inline-start" />Cancel</Button>
                                <Button onClick={save} disabled={saving || !draft?.name.trim()}>
                                    {saving ? <Spinner data-icon="inline-start" /> : <Save data-icon="inline-start" />}Save
                                </Button>
                            </>
                        ) : canEdit ? (
                            <>
                                <Button variant="outline" onClick={() => startEdit(dash)}><Pencil data-icon="inline-start" />Edit</Button>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" aria-label="Dashboard actions"><MoreVertical /></Button></DropdownMenuTrigger>
                                    <DropdownMenuContent align="end"><DropdownMenuGroup>
                                        <DropdownMenuItem variant="destructive" onSelect={() => setDeleteOpen(true)}><Trash2 /> Delete dashboard</DropdownMenuItem>
                                    </DropdownMenuGroup></DropdownMenuContent>
                                </DropdownMenu>
                            </>
                        ) : null}
                    </div>
                </div>
            </div>

            {(saveError || problems.length > 0) && (
                <Alert variant="destructive">
                    <AlertTitle>{saveError || 'Could not save'}</AlertTitle>
                    {problems.length > 0 && <AlertDescription><ul className="list-disc pl-4">{problems.map((p, i) => <li key={i}>{p}</li>)}</ul></AlertDescription>}
                </Alert>
            )}

            <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3" role="group" aria-label="Dashboard filters">
                <Field className="w-auto">
                    <FieldLabel htmlFor="f-period">Period</FieldLabel>
                    <Select value={period} onValueChange={(v) => setPeriod(v as PeriodChoice)}>
                        <SelectTrigger id="f-period" className="w-44"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectGroup>
                            {PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                            <SelectItem value={CUSTOM_PERIOD}>Custom range</SelectItem>
                        </SelectGroup></SelectContent>
                    </Select>
                </Field>
                {period === CUSTOM_PERIOD && (
                    <>
                        <Field className="w-auto" data-invalid={periodProblem ? true : undefined}>
                            <FieldLabel htmlFor="f-from">From</FieldLabel>
                            <DateInput id="f-from" value={from} max={to || undefined} invalid={!!periodProblem} placeholder="Start date" onChange={setFrom} />
                        </Field>
                        <Field className="w-auto" data-invalid={periodProblem ? true : undefined}>
                            <FieldLabel htmlFor="f-to">To</FieldLabel>
                            <DateInput id="f-to" value={to} min={from || undefined} invalid={!!periodProblem} placeholder="End date" onChange={setTo} />
                        </Field>
                    </>
                )}
                {hasFilter(dash.layout, 'department') && (
                    <Field className="w-auto">
                        <FieldLabel htmlFor="f-dept">Business unit</FieldLabel>
                        <Select value={dept || ALL_DEPTS} onValueChange={(v) => setDept(v === ALL_DEPTS ? '' : v)}>
                            <SelectTrigger id="f-dept" className="w-56"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectGroup>
                                <SelectItem value={ALL_DEPTS}>All business units</SelectItem>
                                {depts.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                            </SelectGroup></SelectContent>
                        </Select>
                    </Field>
                )}
                <div className="ml-auto flex items-center gap-3">
                    <span className="hidden text-xs text-muted-foreground sm:inline">
                        You are seeing: {isRiskManager ? 'all submissions' : 'your submissions and ones you review'}
                    </span>
                    <Button variant="outline" onClick={() => refresh()} disabled={widgets.length === 0}><RefreshCw data-icon="inline-start" />Refresh all</Button>
                </div>
            </div>
            <p className="text-xs text-muted-foreground sm:hidden">You are seeing: {isRiskManager ? 'all submissions' : 'your submissions and ones you review'}</p>

            {periodProblem && <p role="alert" className="text-sm text-destructive">{periodProblem}</p>}

            {editing && <p className="flex items-center gap-1 text-sm text-muted-foreground"><Eye className="size-4" aria-hidden />Editing: previews use unsaved changes. Save to publish them to viewers.</p>}

            {widgets.length === 0 ? (
                <Empty>
                    <EmptyHeader>
                        <EmptyMedia variant="icon"><LayoutDashboard /></EmptyMedia>
                        <EmptyTitle>This dashboard is empty</EmptyTitle>
                        <EmptyDescription>{canEdit ? 'Add a widget to start charting submissions.' : 'No widgets have been added yet.'}</EmptyDescription>
                    </EmptyHeader>
                    {canEdit && (
                        <EmptyContent>
                            <Button onClick={() => { if (!editing) startEdit(dash); setEditor({ open: true, widget: null }) }}><Plus data-icon="inline-start" />Add widget</Button>
                        </EmptyContent>
                    )}
                </Empty>
            ) : (
                <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                    {widgets.map((w, i) => (
                        <li key={w.id} className={SPAN_CLASS[defaultSpan(w.type)]}>
                            <WidgetTile
                                widget={w}
                                slot={slots[w.id]}
                                editing={editing}
                                canMoveUp={i > 0}
                                canMoveDown={i < widgets.length - 1}
                                onMove={(d) => setWidgets((list) => moveItem(list, i, d))}
                                onRefresh={() => refresh(w)}
                                onEdit={() => setEditor({ open: true, widget: w })}
                                onDuplicate={() => setWidgets((list) => {
                                    const copy = { ...w, id: newWidgetId(), title: `${w.title} (copy)` }
                                    return [...list.slice(0, i + 1), copy, ...list.slice(i + 1)]
                                })}
                                onRemove={() => setRemoveId(w.id)}
                                onExport={editing ? undefined : () => exportCsv(w)}
                                onOpenRow={openRow}
                            />
                        </li>
                    ))}
                </ul>
            )}

            <WidgetEditor
                open={editor.open}
                onOpenChange={(o) => setEditor((e) => ({ ...e, open: o }))}
                initial={editor.widget}
                forms={forms}
                onSave={(w) => {
                    setWidgets((list) => (list.some((x) => x.id === w.id) ? list.map((x) => (x.id === w.id ? w : x)) : [...list, w]))
                    setEditor({ open: false, widget: null })
                }}
            />

            <Dialog open={removeId !== null} onOpenChange={(o) => !o && setRemoveId(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Remove widget?</DialogTitle>
                        <DialogDescription>"{removeTarget?.title}" will be removed when you save the dashboard.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRemoveId(null)}>Keep</Button>
                        <Button variant="destructive" onClick={() => { setWidgets((l) => l.filter((x) => x.id !== removeId)); setRemoveId(null) }}>Remove</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete this dashboard?</DialogTitle>
                        <DialogDescription>"{dash.name}" and its widgets will be permanently deleted. This cannot be undone.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={remove} disabled={deleting}>{deleting && <Spinner data-icon="inline-start" />}Delete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
